"use client";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { contactInfo } from "@/lib/contacts";
import { useModalCustomerContext } from "@/context/CustomerContext";
import { useModalInquiryContext } from "@/context/ModalInquiryContext";

interface FullPageScrollProps {
	children: React.ReactNode;
	pageLabels?: string[];
}

export default function FullPageScroll({ children, pageLabels = [] }: FullPageScrollProps) {
	const containerRef = useRef<HTMLDivElement>(null);
	const [currentPage, setCurrentPage] = useState(0);
	const [isDesktopViewport, setIsDesktopViewport] = useState(false);
	const isScrollingRef = useRef(false);
	const rafIdRef = useRef<number | null>(null);
	const lastPageRef = useRef(0);
	const childrenArray = React.Children.toArray(children);
	const t = useTranslations("menu");
	const tPlsyy = useTranslations("plsyy");
	const navT = useTranslations("nav");
	const { openModalCustomer, isCustomerModalOpen } = useModalCustomerContext();
	const { openModalInquiry } = useModalInquiryContext();
	
	// 默认页面标签
	const defaultLabels = [
		t("Home"),
		tPlsyy("Product") + " " + tPlsyy("Series"),
		tPlsyy("Industry") + " " + tPlsyy("Applications"),
		tPlsyy("About-Title") + " " + tPlsyy("About-Title-Highlight"),
		tPlsyy("Advantage-Title"),
		tPlsyy("Featured")+ " " + tPlsyy("News"),
		t("Contact Us")
	];
	
	const labels = pageLabels.length > 0 ? pageLabels : defaultLabels.slice(0, childrenArray.length);
	
	// SliderBar的items配置（与SliderBar组件保持一致）
	const sidebarItems = [
		{ 
			icon: "ri-phone-fill", 
			text: navT("Phone"), 
			info: contactInfo.phone,
			link: `tel:${contactInfo.phone}`,
			type: "link"
		},
		{ 
			icon: "ri-whatsapp-fill", 
			text: navT("WhatsApp"), 
			info: contactInfo.whatsapp,
			link: `https://wa.me/${contactInfo.whatsapp.replace(/[^0-9]/g, "")}`,
			type: "link"
		},
		{
			icon: "ri-mail-fill",
			text: navT("Email"),
			info: contactInfo.email,
			link: `mailto:${contactInfo.email}`,
			type: "link"
		},
		{
			icon: "ri-wechat-fill",
			text: navT("Wechat"),
			info: contactInfo.wechat,
			link: `https://web.weixin.qq.com/`,
			type: "link"
		},
		{ 
			icon: "ri-customer-service-fill", 
			text: navT("Customer-Service"), 
			info: "",
			link: "#",
			type: "customer"
		},
		{ 
			icon: "ri-file-list-2-line", 
			text: navT("Inquiry"), 
			info: "",
			link: "#",
			type: "inquiry"
		},
		{ 
			icon: "ri-arrow-up-circle-fill", 
			text: navT("Back-to-Top"), 
			info: "",
			link: "#",
			type: "scroll"
		},
	];

	const handleSidebarItemClick = (item: any, e: React.MouseEvent) => {
		if (item.type === "scroll") {
			e.preventDefault();
			containerRef.current?.scrollTo({
				top: 0,
				behavior: "smooth"
			});
		} else if (item.type === "customer") {
			e.preventDefault();
			openModalCustomer();
		} else if (item.type === "inquiry") {
			e.preventDefault();
			openModalInquiry(false);
		} else if (item.type === "wechat") {
			e.preventDefault();
			window.open("https://web.weixin.qq.com/", "_blank");
		}
	};
	
	// 缓存容器高度，避免频繁读取导致强制重排
	const cachedHeightRef = useRef<number | null>(null);

	useEffect(() => {
		const mediaQuery = window.matchMedia("(min-width: 768px)");
		const updateViewport = () => {
			setIsDesktopViewport(mediaQuery.matches);
		};

		updateViewport();
		mediaQuery.addEventListener("change", updateViewport);

		return () => {
			mediaQuery.removeEventListener("change", updateViewport);
		};
	}, []);
	
	// 点击导航项跳转到对应页面
	const scrollToPage = (pageIndex: number) => {
		const container = containerRef.current;
		if (!container || isScrollingRef.current) return;
		
		if (pageIndex >= 0 && pageIndex < childrenArray.length) {
			isScrollingRef.current = true;
			
			// 使用缓存的高度，避免强制重排
			// 只在缓存无效时读取（首次或窗口大小变化后）
			if (cachedHeightRef.current === null) {
				cachedHeightRef.current = container.clientHeight;
			}
			const sectionHeight = cachedHeightRef.current;
			const targetScroll = pageIndex * sectionHeight;
			
			// 立即更新页面索引，避免滚动过程中的延迟感
			setCurrentPage(pageIndex);
			lastPageRef.current = pageIndex;
			
			container.scrollTo({
				top: targetScroll,
				behavior: 'smooth'
			});
			
			// 缩短锁定时间，让滚动更流畅
			setTimeout(() => {
				isScrollingRef.current = false;
			}, 300);
		}
	};

	useLayoutEffect(() => {
		if (!isDesktopViewport) {
			document.documentElement.classList.remove('full-page-scroll-active');
			return;
		}

		// 挂载时锁定根节点高度，确保翻页效果生效（layout effect 避免首屏布局偏移）
		document.documentElement.classList.add('full-page-scroll-active');
		return () => {
			document.documentElement.classList.remove('full-page-scroll-active');
		};
	}, [isDesktopViewport]);

	useEffect(() => {
		if (!isDesktopViewport) {
			return;
		}

		// 使用 requestAnimationFrame 优化滚动事件处理，减少卡顿
		const handleScroll = () => {
			// 取消之前的 RAF
			if (rafIdRef.current !== null) {
				cancelAnimationFrame(rafIdRef.current);
			}

			// 使用 RAF 节流，确保在浏览器重绘前执行
			rafIdRef.current = requestAnimationFrame(() => {
				const container = containerRef.current;
				if (!container || isScrollingRef.current) return;

				const scrollTop = container.scrollTop;
				// 使用缓存的高度，避免强制重排
				if (cachedHeightRef.current === null) {
					cachedHeightRef.current = container.clientHeight;
				}
				const sectionHeight = cachedHeightRef.current;
				// 使用更精确的计算，考虑滚动位置的中心点
				const page = Math.round(scrollTop / sectionHeight);
				
				// 只在页面真正改变时才更新状态，减少不必要的重新渲染
				if (page !== lastPageRef.current && page >= 0 && page < childrenArray.length) {
					lastPageRef.current = page;
					setCurrentPage(page);

					// 当滚动到第二个页面（index 1，即 Category）或之后时，触发 isVisible
					if (page >= 1) {
						window.dispatchEvent(new CustomEvent('pageScroll', { detail: { isVisible: true, currentPage: page } }));
					} else {
						window.dispatchEvent(new CustomEvent('pageScroll', { detail: { isVisible: false, currentPage: page } }));
					}
				}
			});
		};

		const container = containerRef.current;
		if (container) {
			container.addEventListener("scroll", handleScroll, { passive: true });
			handleScroll();
		}

		return () => {
			if (container) {
				container.removeEventListener("scroll", handleScroll);
			}
			if (rafIdRef.current !== null) {
				cancelAnimationFrame(rafIdRef.current);
			}
		};
	}, [childrenArray.length, isDesktopViewport]);

	useEffect(() => {
		if (!isDesktopViewport) {
			return;
		}

		const container = containerRef.current;
		if (!container) return;

		// 监听窗口大小变化，更新缓存的高度，避免强制重排
		const handleResize = () => {
			cachedHeightRef.current = null; // 清除缓存，下次读取时重新计算
		};
		
		// 使用防抖优化resize事件
		let resizeTimer: NodeJS.Timeout;
		const resizeHandler = () => {
			clearTimeout(resizeTimer);
			resizeTimer = setTimeout(handleResize, 200);
		};
		
		window.addEventListener('resize', resizeHandler, { passive: true });
		
		return () => {
			window.removeEventListener('resize', resizeHandler);
			if (resizeTimer) clearTimeout(resizeTimer);
		};
	}, [childrenArray.length, isDesktopViewport]);

	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		// 移除手动拦截 wheel 的逻辑，改回依靠 CSS scroll-snap
		// 这样可以解决在 zoom 模式下，手动滚动计算不准导致的“翻页失败”
		// 同时也更丝滑，支持触控板和平滑滚动鼠标
		
		return () => {
			// 
		};
	}, [childrenArray.length]);

	return (
		<div className="relative h-full overflow-hidden">
			{/* 右侧中间按钮 - hover显示SliderBar */}
			<div className="fixed right-2 bottom-1/2 z-50 hidden md:block">
				<div className="group relative flex items-center">
					{/* hover时显示的SliderBar内容 - 从右侧滑入 */}
					{!isCustomerModalOpen && (
						<div className="absolute right-0 top-1/2 -translate-y-1/2 opacity-0 translate-x-8 scale-95 group-hover:opacity-100 group-hover:translate-x-0 group-hover:scale-100 transition-all duration-500 ease-out pointer-events-none group-hover:pointer-events-auto z-20">
							<div className="flex flex-col">
								{sidebarItems.map((item, index) => (
									<div
										key={index}
										className="group/item relative mb-3 opacity-0 translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 ease-out"
										style={{
											transitionDelay: `${index * 0.05}s`
										}}
									>
										{item.type === "link" ? (
											<>
												{/* 联系方式 - hover时从右侧向左延伸 */}
												{item.info && (
													<div className="rounded-tl-full rounded-bl-full absolute right-full top-0 h-[68px] flex items-center px-6 py-2.5 bg-mainColor text-white whitespace-nowrap z-10 shadow-lg overflow-hidden w-0 opacity-0 pointer-events-none group-hover/item:w-[260px] group-hover/item:opacity-100 group-hover/item:pointer-events-auto transition-all duration-500 ease-out mr-3">
														<span className="text-[17px] font-medium opacity-0 group-hover/item:opacity-100 transition-opacity duration-300 delay-200 w-full">{item.info}</span>
													</div>
												)}
												{/* 按钮本身 */}
												<a
													href={item.link}
													target="_blank"
													rel="noopener noreferrer"
													aria-label={`${item.text}: ${item.info || ''}`}
													className="flex items-center justify-center w-[68px] h-[68px] bg-black/30 hover:bg-mainColor cursor-pointer transition-all duration-300 relative z-30 rounded-full shadow-md hover:shadow-lg hover:scale-110 active:scale-95"
												>
													<i className={`${item.icon} text-[34px] text-white transition-all duration-300`} aria-hidden="true"></i>
												</a>
											</>
										) : (
											<button
												type="button"
												onClick={(e) => handleSidebarItemClick(item, e)}
												aria-label={item.text}
												className="flex items-center justify-center w-[68px] h-[68px] bg-black/30 hover:bg-mainColor cursor-pointer transition-all duration-300 rounded-full shadow-md hover:shadow-lg hover:scale-110 active:scale-95 relative z-30"
											>
												<i className={`${item.icon} text-[34px] text-white transition-all duration-300`} aria-hidden="true"></i>
											</button>
										)}
									</div>
								))}
							</div>
						</div>
					)}
					
					{/* 默认按钮 - hover时逐渐消失 */}
					<button
						type="button"
						aria-label="Open contact menu"
						className="flex items-center justify-center border-white/20 border w-[68px] h-[68px] bg-black/30 hover:bg-mainColor cursor-pointer transition-all duration-500 ease-out rounded-full shadow-lg relative z-10 group-hover:opacity-0 group-hover:scale-75 group-hover:pointer-events-none"
					>
						<i className="ri-menu-line text-[34px] text-white transition-transform duration-500 group-hover:rotate-180" aria-hidden="true"></i>
					</button>
					
					{/* 扩大hover区域，连接按钮和SliderBar，防止鼠标移动时消失 - 不拦截点击事件 */}
					<div className="absolute right-0 top-1/2 -translate-y-1/2 w-[300px] h-[760px] pointer-events-none z-0"></div>
				</div>
			</div>

			{/* 垂直导航条 */}
			<div className="fixed left-2 bottom-1/4 z-50 hidden md:block">
				<div className="relative flex flex-col items-center">
					{labels.map((label, index) => {
						const isActive = currentPage === index;
						const isLast = index === labels.length - 1;
						// 只在激活点位和下一个点位之间显示蓝色连接线
						const showActiveLine = isActive && !isLast;
						
						return (
							<div key={index} className="relative flex flex-col items-center mb-2">
								{/* 导航项容器 - 点位固定在左侧 */}
								<div className="relative flex items-center justify-start w-full group/item" style={{ minHeight: '20px' }}>
									{/* 圆形指示器 - 固定位置 */}
									<button
										onClick={() => scrollToPage(index)}
										className="group relative flex items-center cursor-pointer"
										aria-label={`Go to ${label}`}
									>
										<div
											className={`relative rounded-full z-10 transition-all duration-500 ease-in-out border-white/20 border ${
												isActive
													? 'w-4 h-4 bg-mainColor shadow-md shadow-mainColor/50'
													: 'w-3 h-3 bg-gray-300 group-hover:bg-mainColor group-hover:w-4 group-hover:h-4'
											}`}
										/>
									</button>
									
									{/* 文字标签 - 仅在hover时显示 */}
									<span 
										className="absolute left-8 text-sm font-semibold text-mainColor whitespace-nowrap transition-all duration-500 ease-in-out opacity-0 -translate-x-2 pointer-events-none group-hover/item:opacity-100 group-hover/item:translate-x-0"
										style={{
											textShadow: `
											-0.5px -0.5px 0 #fff,
											0.5px -0.5px 0 #fff,
											-0.5px 0.5px 0 #fff,
											0.5px 0.5px 0 #fff,
											0 0 2px rgba(255, 255, 255, 0.2)
											`
										}}
									>
										{label}
									</span>
								</div>
								
								{/* 连接线 - 仅在激活点位和下一个点位之间显示，从圆圈中心垂直连接 */}
								{showActiveLine && (
									<div 
										className="w-[2px] bg-mainColor"
										style={{ 
											height: '64px',
											marginTop: '12px',
											marginBottom: '8px',
											animation: 'lineGrow 0.6s ease-out forwards',
											transformOrigin: 'top center'
										}}
									/>
								)}
							</div>
						);
					})}
				</div>
			</div>
			
			<div
				ref={containerRef}
				className="fp-container"
				style={{
					scrollBehavior: "smooth",
					scrollbarWidth: "none",
					msOverflowStyle: "none",
					willChange: "scroll-position",
					transform: "translateZ(0)", // 启用硬件加速
				}}
			>
				<style dangerouslySetInnerHTML={{__html: `
					div::-webkit-scrollbar {
						display: none;
					}
					@keyframes fadeInDown {
						from {
							opacity: 0;
							transform: translateY(-10px);
						}
						to {
							opacity: 1;
							transform: translateY(0);
						}
					}
					@keyframes lineGrow {
						from {
							transform: scaleY(0);
							opacity: 0;
						}
						to {
							transform: scaleY(1);
							opacity: 1;
						}
					}
					@keyframes fadeInRight {
						from {
							opacity: 0;
							transform: translateX(20px);
						}
						to {
							opacity: 1;
							transform: translateX(0);
						}
					}
					@media (min-width: 768px) {
						.fp-container {
							height: var(--zoom-compensated-vh, 100vh);
							width: 100%;
							overflow-y: scroll;
							scroll-snap-type: y mandatory;
							-webkit-overflow-scrolling: touch;
							overscroll-behavior: contain;
						}
						.fp-section {
							height: var(--zoom-compensated-vh, 100vh);
							width: 100%;
							flex-shrink: 0;
							scroll-snap-align: start;
							scroll-snap-stop: always;
							will-change: transform;
							transform: translateZ(0);
							min-height: var(--zoom-compensated-vh, 100vh);
							contain: layout style paint;
						}
					}
					@media (max-width: 767px) {
						.fp-container {
							height: auto;
							width: 100%;
							overflow-y: visible;
							overflow-x: hidden;
						}
						.fp-section {
							height: auto;
							width: 100%;
							min-height: 50vh;
							contain: layout style paint;
						}
					}
				`}} />
				{React.Children.map(children, (child, index) => (
					<div
						key={index}
						className="fp-section"
						style={{
							willChange: "transform",
							transform: "translateZ(0)",
						}}
					>
						{child}
					</div>
				))}
			</div>
		</div>
	);
}

